import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import { portalFetch } from "../api/client";
import { clearInvitationSecret, invitationSecretBody } from "../invitations/bearerSecret";
import type { PortalMeDto, PortalPasskeyDto } from "../model";
import { clearPortalActor, setPortalActor } from "./session";

export type OrganizationInvitationPreview = {
	organizationName: string;
	intendedRecipientLabel: string;
	offeredRole: "administrator" | "member";
	allProjects: boolean;
	projects: { id: string; name: string }[];
	expiresAt: string;
};

export function previewOrganizationInvitation() {
	return portalFetch<OrganizationInvitationPreview>("/api/portal/v1/auth/organization-invitations/preview", {
		method: "POST",
		body: invitationSecretBody(),
	});
}

export async function createIdentityForOrganizationInvitation(displayName: string) {
	const first = await portalFetch<{ options: Parameters<typeof startRegistration>[0]["optionsJSON"] }>(
		"/api/portal/v1/auth/organization-invitations/options",
		{ method: "POST", body: invitationSecretBody({ displayName }) },
	);
	const response = await startRegistration({ optionsJSON: first.options });
	const actor = await portalFetch<PortalMeDto>("/api/portal/v1/auth/organization-invitations/verify", {
		method: "POST",
		body: invitationSecretBody({ response }),
	});
	clearInvitationSecret();
	setPortalActor(actor);
	return actor;
}
export async function acceptOrganizationInvitationForCurrentIdentity(expectedPortalUserId: string) {
	const actor = await portalFetch<PortalMeDto>("/api/portal/v1/organization-invitations/accept", {
		method: "POST",
		body: invitationSecretBody({ expectedPortalUserId }),
	});
	clearInvitationSecret();
	setPortalActor(actor);
	return actor;
}
export async function claimInvitation(invitationCode: string) {
	const first = await portalFetch<{ options: Parameters<typeof startRegistration>[0]["optionsJSON"] }>(
		"/api/portal/v1/auth/invitations/options",
		{ method: "POST", body: JSON.stringify({ invitationCode }) },
	);
	const response = await startRegistration({ optionsJSON: first.options });
	const actor = await portalFetch<PortalMeDto>("/api/portal/v1/auth/invitations/verify", {
		method: "POST",
		body: JSON.stringify({ invitationCode, response }),
	});
	setPortalActor(actor);
	return actor;
}
export async function loginWithPasskey() {
	const first = await portalFetch<{ options: Parameters<typeof startAuthentication>[0]["optionsJSON"] }>(
		"/api/portal/v1/auth/login/options",
		{ method: "POST", body: "{}" },
	);
	const response = await startAuthentication({ optionsJSON: first.options });
	const actor = await portalFetch<PortalMeDto>("/api/portal/v1/auth/login/verify", {
		method: "POST",
		body: JSON.stringify({ response }),
	});
	setPortalActor(actor);
	return actor;
}
export async function addPortalPasskey() {
	const confirmation = await portalFetch<{
		options: Parameters<typeof startAuthentication>[0]["optionsJSON"];
	}>("/api/portal/v1/auth/login/options", { method: "POST", body: "{}" });
	const authenticationResponse = await startAuthentication({ optionsJSON: confirmation.options });
	const first = await portalFetch<{ options: Parameters<typeof startRegistration>[0]["optionsJSON"] }>(
		"/api/portal/v1/auth/passkeys/options",
		{ method: "POST", body: JSON.stringify({ response: authenticationResponse }) },
	);
	const response = await startRegistration({ optionsJSON: first.options });
	return portalFetch<PortalPasskeyDto>("/api/portal/v1/auth/passkeys/verify", {
		method: "POST",
		body: JSON.stringify({ response }),
	});
}
export async function logoutPortal() {
	try {
		await portalFetch<void>("/api/portal/v1/auth/logout", { method: "POST", body: "{}" });
	} finally {
		clearPortalActor();
	}
}
